import { useMemo } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Loader from "components/DesignSystem/Loader/loader";
import { useGetSnoozedChaseItemsQuery } from "store/apis/agents";
import { SnoozedChaseItem } from "types/Models/agentDashboard";
import { openLink } from "utils/openLink";
import { pluralize } from "utils/pluralize";
import {
  CHASE_TYPE_LABELS,
  COLUMN_CLASSES,
  GroupTriggerRow,
  filterChaseItems,
  ChatTarget,
} from "./ChaserDashboard";

dayjs.extend(relativeTime);

const COUNTDOWN_COLUMN_CLASS = "t-w-40 t-shrink-0 t-text-right";

type SnoozedItemsSectionProps = {
  agentUserIds: string | null;
  isAgentSelectionReady: boolean;
  selectedTypes: string[];
  newOnly: boolean;
  onNavigate: (target: ChatTarget) => void;
};

const getSnoozedItemTarget = (item: SnoozedChaseItem) => {
  if (item.item_channel_url) {
    // link format: {base_url}chat/{channel_url}/{message_id}
    const afterChannel = item.link?.split(`${item.item_channel_url}/`)[1];
    const messageId = afterChannel?.split("/")[0] || undefined;
    return {
      type: "chat" as const,
      channelId: item.item_channel_url,
      messageId,
    };
  }
  return item.link ? { type: "url" as const, url: item.link } : null;
};

const SnoozedColumnHeaders = () => (
  <div className="t-flex t-items-center t-gap-4 t-px-4 t-py-2 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey">
    <span
      className={classNames(
        COLUMN_CLASSES.chaseCount,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Chased
    </span>
    <span
      className={classNames(
        COLUMN_CLASSES.actionType,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Action Type
    </span>
    <span
      className={classNames(
        COLUMN_CLASSES.description,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Description
    </span>
    <span
      className={classNames(
        COUNTDOWN_COLUMN_CLASS,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Countdown
    </span>
  </div>
);

const SnoozedItemRow = ({
  item,
  onNavigate,
}: {
  item: SnoozedChaseItem;
  onNavigate: (target: ChatTarget) => void;
}) => {
  const label = CHASE_TYPE_LABELS[item.metric_type] ?? item.metric_type;
  const target = getSnoozedItemTarget(item);

  const handleClick = () => {
    if (!target) return;
    if (target.type === "chat") {
      onNavigate({ channelId: target.channelId, messageId: target.messageId });
    } else {
      openLink(target.url, "_blank");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (target && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={classNames(
        "t-flex t-items-center t-gap-4 t-px-4 t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-10 last:t-border-b-0 hover:t-bg-surface-lighter-grey",
        { "t-cursor-pointer": !!target }
      )}
      onClick={target ? handleClick : undefined}
      onKeyDown={target ? handleKeyDown : undefined}
      role={target ? "button" : undefined}
      tabIndex={target ? 0 : undefined}
    >
      <span
        className={classNames(
          COLUMN_CLASSES.chaseCount,
          "t-text-body-sm t-text-text-60"
        )}
      >
        {item.chase_count ?? 0}
      </span>
      <span
        className={classNames(
          COLUMN_CLASSES.actionType,
          "t-text-body-sm t-text-text-60"
        )}
      >
        {label}
      </span>
      <span
        className={classNames(
          COLUMN_CLASSES.description,
          "t-text-body-sm t-text-text-80 t-truncate"
        )}
      >
        {item.detail || "—"}
      </span>
      <span
        className={classNames(
          COUNTDOWN_COLUMN_CLASS,
          "t-text-body-sm t-text-text-30"
        )}
      >
        Sends again {dayjs(item.snoozed_until).fromNow()}
      </span>
    </div>
  );
};

export const SnoozedItemsSection = ({
  agentUserIds,
  isAgentSelectionReady,
  selectedTypes,
  newOnly,
  onNavigate,
}: SnoozedItemsSectionProps) => {
  const { data: groups = [], isFetching } = useGetSnoozedChaseItemsQuery(
    agentUserIds ? { agentUserIds } : undefined,
    { skip: !isAgentSelectionReady }
  );

  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          items: filterChaseItems(group.items, selectedTypes, newOnly),
        }))
        .filter((group) => group.items.length > 0),
    [groups, selectedTypes, newOnly]
  );

  if (!isAgentSelectionReady || isFetching) {
    return <Loader />;
  }

  return (
    <div className="t-border t-border-solid t-border-neutral-10 t-rounded-xl t-overflow-hidden">
      <section className="t-p-4">
        <p className="t-text-caption t-font-medium t-text-text-30 t-uppercase t-tracking-wide t-mb-3">
          Snoozed Items
        </p>
        {filteredGroups.length > 0 ? (
          <Accordion.Root
            type="single"
            collapsible
            className="t-flex t-flex-col t-gap-3"
          >
            {filteredGroups.map(({ company_group, items }) => {
              const agentSuffix = company_group.agent_name
                ? ` · ${company_group.agent_name}`
                : "";
              return (
                <Accordion.Item
                  key={company_group.uuid}
                  value={company_group.uuid}
                >
                  <Accordion.Header className="t-m-0 t-flex t-items-stretch">
                    <GroupTriggerRow>
                      <div className="t-flex t-items-center t-gap-1 t-min-w-0">
                        <span className="t-text-subtext t-font-semibold t-text-text-100">
                          {company_group.name}
                        </span>
                        {agentSuffix && (
                          <span className="t-text-body-sm t-text-text-30">
                            {agentSuffix}
                          </span>
                        )}
                      </div>
                      <span className="t-text-body-sm t-text-text-30 t-shrink-0">
                        {pluralize(items.length, "Item", "Items")}
                      </span>
                    </GroupTriggerRow>
                  </Accordion.Header>
                  <Accordion.Content className="t-p-0">
                    <SnoozedColumnHeaders />
                    <div className="t-flex t-flex-col">
                      {items.map((item) => (
                        <SnoozedItemRow
                          key={`${item.metric_type}-${item.item_uuid}`}
                          item={item}
                          onNavigate={onNavigate}
                        />
                      ))}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              );
            })}
          </Accordion.Root>
        ) : (
          <div className="t-text-body-sm t-text-text-30 t-py-2">
            0 snoozed items
          </div>
        )}
      </section>
    </div>
  );
};
